import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSessionFilter } from '../hooks/useSessionFilter';
import { useColumnConfig, useModuleVisibility } from '../hooks/useConfigVisibility';
import { anfragenApi, type AnfrageCreateData, type AnfrageQueryParams } from '../api/anfragen';
import AnfragenStateBadge from '../components/AnfragenStateBadge';
import { ANFRAGE_STATES } from '../types';
import FilterDropdown from '../components/FilterDropdown';
import SortTh from '../components/SortTh';
import Spinner from '../components/Spinner';
import EmptyTableRow from '../components/EmptyTableRow';
import Pagination from '../components/Pagination';

export default function AnfragenList() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  // URL-Parameter ?status=... haben Vorrang (z. B. bei Links vom Dashboard)
  const urlStatus = searchParams.get('status');
  const [searchText, setSearchText] = useSessionFilter<string>('anfragen_searchText', '');
  const [statusFilter, setStatusFilter] = useSessionFilter<string | null>(
    'anfragen_statusFilter',
    null,
    urlStatus !== null ? urlStatus : undefined,
  );
  const [sortBy, setSortBy] = useSessionFilter<string>('anfragen_sortBy', 'created_at');
  const [sortDir, setSortDir] = useSessionFilter<'asc' | 'desc'>('anfragen_sortDir', 'desc');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState<AnfrageCreateData>({ strasse: '', hausnummer: '', plz: '', city: '' });
  const [createError, setCreateError] = useState('');
  const cols = useColumnConfig('anfragen');
  const pv = useModuleVisibility();
  const perPage = 50;

  const queryParams: AnfrageQueryParams = {
    page,
    per_page: perPage,
    search_text: searchText || undefined,
    status: statusFilter || undefined,
    sort_by: sortBy,
    sort_dir: sortDir,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['anfragen', queryParams],
    queryFn: () => anfragenApi.getAnfragen(queryParams),
  });

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  const createMutation = useMutation({
    mutationFn: (body: AnfrageCreateData) => anfragenApi.createAnfrage(body),
    onSuccess: (anfrage) => {
      queryClient.invalidateQueries({ queryKey: ['anfragen'] });
      navigate(`/anfragen/${anfrage.id}`);
    },
    onError: () => {
      setCreateError('Fehler beim Erstellen der Anfrage.');
    },
  });

  const handleCreate = () => {
    if (!form.strasse.trim() || !form.hausnummer.trim()) {
      setCreateError('Strasse und Hausnummer sind erforderlich.');
      return;
    }
    setCreateError('');
    createMutation.mutate({
      strasse: form.strasse.trim(),
      hausnummer: form.hausnummer.trim(),
      plz: form.plz?.trim() || undefined,
      city: form.city?.trim() || undefined,
    });
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const inputClass = "w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";

  // "Offen" = all states except final ones
  const isOffeneActive = statusFilter === 'offene';

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Search + Filter + New button */}
        <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
          <div className="flex gap-3">
            <FilterDropdown label="Status" badgeCount={statusFilter ? 1 : 0}>
              <div className="space-y-0.5">
                <button
                  onClick={() => { setStatusFilter(isOffeneActive ? null : 'offene'); setPage(1); }}
                  className={`w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                    isOffeneActive ? 'bg-gray-900 text-white' : 'hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  Offen
                </button>
                {Object.entries(ANFRAGE_STATES).map(([key, { label }]) => (
                  <button
                    key={key}
                    onClick={() => { setStatusFilter(statusFilter === key ? null : key); setPage(1); }}
                    className={`w-full text-left px-3 py-1.5 rounded-lg text-sm ${
                      statusFilter === key ? 'bg-gray-900 text-white' : 'hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </FilterDropdown>

            <input
              type="text"
              placeholder="Suche nach Adresse, Notizen..."
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            />
            <button
              onClick={() => { setShowCreateForm(true); setCreateError(''); }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              + Neue Anfrage
            </button>
          </div>

          {/* Active filter chips */}
          {statusFilter && (
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 border border-blue-200 text-blue-800">
                Status: {statusFilter === 'offene' ? 'Offen' : ANFRAGE_STATES[statusFilter]?.label}
                <button
                  onClick={() => { setStatusFilter(null); setPage(1); }}
                  className="text-blue-400 hover:text-blue-600 leading-none"
                >
                  ×
                </button>
              </span>
            </div>
          )}
        </div>

        {/* Create form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h3 className="text-base font-medium text-gray-800 mb-4">Neue Anfrage anlegen</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Strasse *</label>
                <input
                  value={form.strasse}
                  onChange={(e) => setForm({ ...form, strasse: e.target.value })}
                  placeholder="z.B. Musterstraße"
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Hausnummer *</label>
                <input
                  value={form.hausnummer}
                  onChange={(e) => setForm({ ...form, hausnummer: e.target.value })}
                  placeholder="z.B. 5a"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">PLZ</label>
                <input
                  value={form.plz}
                  onChange={(e) => setForm({ ...form, plz: e.target.value })}
                  placeholder="z.B. 10115"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Stadt</label>
                <input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="z.B. Berlin"
                  className={inputClass}
                />
              </div>
            </div>
            {createError && (
              <p className="text-sm text-red-600 mt-2">{createError}</p>
            )}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {createMutation.isPending ? 'Wird angelegt...' : 'Anlegen'}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setForm({ strasse: '', hausnummer: '', plz: '', city: '' }); setCreateError(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortTh field="adresse_text" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Adresse</SortTh>
                    {cols.status !== false && <SortTh field="status" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Status</SortTh>}
                    {cols.zuordnung !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zuordnung</th>}
                    {cols.notizen !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notizen</th>}
                    {cols.created_at !== false && <SortTh field="created_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Erstellt</SortTh>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {!data?.items || data.items.length === 0 ? (
                    <EmptyTableRow colSpan={5} message="Keine Anfragen gefunden." />
                  ) : (
                    data.items.map((anfrage) => (
                        <tr
                          key={anfrage.id}
                          onClick={() => navigate(`/anfragen/${anfrage.id}`)}
                          className="hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="px-3 py-4 text-sm font-medium text-gray-900">
                            {anfrage.adresse_text || '—'}
                            {anfrage.zipcode && <span className="text-gray-500 ml-1">{anfrage.zipcode}</span>}
                            {anfrage.city && <span className="text-gray-500 ml-1">{anfrage.city}</span>}
                          </td>
                          {cols.status !== false && (
                            <td className="px-3 py-4 text-sm">
                              <AnfragenStateBadge status={anfrage.status} />
                            </td>
                          )}
                          {cols.zuordnung !== false && (
                            <td className="px-3 py-4 text-sm text-gray-500">
                              <div className="flex gap-1 flex-wrap">
                                {anfrage.adresse_id && pv.adressen !== false && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-800">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                    Adresse
                                  </span>
                                )}
                                {anfrage.verfahren_id && pv.verfahren !== false && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-800">
                                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                                    Verfahren
                                  </span>
                                )}
                                {anfrage.vermieter_id && pv.vermieter !== false && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-teal-100 text-teal-800">
                                    <span className="w-1.5 h-1.5 rounded-full bg-teal-500" />
                                    Vermieter
                                  </span>
                                )}
                              </div>
                            </td>
                          )}
                          {cols.notizen !== false && (
                            <td className="px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                              {anfrage.notes || '—'}
                            </td>
                          )}
                          {cols.created_at !== false && (
                            <td className="px-3 py-4 text-sm text-gray-500 whitespace-nowrap">
                              {new Date(anfrage.created_at).toLocaleDateString('de-DE')}
                            </td>
                          )}
                        </tr>
                      ))
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={data?.total ?? 0}
              entityLabel="Anfragen"
              onPageChange={setPage}
            />
          </>
        )}
      </main>
    </div>
  );
}
